import {
  type Location, type IDateRange,
  type IStorageObject,
  TimeRange,
  Timezone,
  Weekday
} from './common'
import { type IUserProfile } from './user'

export interface RequestStudioBookingInput {
  studioId: string
  requesterUserId: string
  startTime: Date
  endTime: Date
}

export interface StudioMetadata {
  id: string
  ownerId: string
  title: string
  rate: number
  maxOccupancy: number
  coverPicture: IStorageObject
  location: {
    city: string
    state: string
  }
}

export enum StudioStatus {
  ACTIVE = 'ACTIVE',
  PENDING_RESOLVE_ACTIONS = 'PENDING_RESOLVE_ACTIONS',
  DELISTED = 'DELISTED'
}

export enum StudioPendingAction {
  STRIPE_ACCOUNT_ONBOARDING = 'STRIPE_ACCOUNT_ONBOARDING'
}

export interface IStudioModel {
  id: string
  status: StudioStatus
  ownerId: string
  info: StudioInfo
}

export interface IStudioModelWithAvailability extends IStudioModel {
  availability: Availability
}

export interface IStudio extends IStudioModel {
  metadata: StudioMetadata
  getOwnerProfile: () => Promise<IUserProfile>
  getAvailability: () => Promise<Availability>
}

export interface StudioInfo {
  title: string
  description: string
  rate: number
  minSessionMinutes: number
  maxOccupancy: number
  studioPictures: IStorageObject[]
  location: Location
}

export interface AvailabilityException {
  timeRange: IDateRange
  isAvailable: boolean
}

export interface StandardAvailability {
  timezone: Timezone
  [Weekday.MONDAY]?: TimeRange[]
  [Weekday.TUESDAY]?: TimeRange[]
  [Weekday.WEDNESDAY]?: TimeRange[]
  [Weekday.THURSDAY]?: TimeRange[]
  [Weekday.FRIDAY]?: TimeRange[]
  [Weekday.SATURDAY]?: TimeRange[]
  [Weekday.SUNDAY]?: TimeRange[]
}

export interface Availability {
  standardAvailability: StandardAvailability
  exceptions: AvailabilityException[]
}

export interface CreateStudioStudioInfo extends Omit<StudioInfo, 'studioPictures'> {
  studioPictures: File[]
}

export interface CreateStudioInput {
  ownerUserId: string
  info: CreateStudioStudioInfo
  availability: Availability
}

// All fields are optional.
// If a field is not provided, it will not be updated and will retain its previous value
export type UpdateStudioInput = Record<string, unknown> & Omit<CreateStudioInput, 'ownerUserId'>
